import fonts from 'partner-fonts';

/**
 * Publishes the active partner's font layer to the document:
 *
 *  - one @font-face per face the partner actually uses, and
 *  - the four font stacks as CSS custom properties on :root.
 *
 * The custom properties are the point. Anything that needs the partner's
 * fonts — MUI variants, a styled() block, a raw `sx` rule, a plain stylesheet
 * — reads `var(--font-body)` and gets the right answer, with no dependency on
 * the MUI theme and nothing to keep in sync.
 *
 * Called from src/index.tsx before the first render, so the variables are
 * defined before anything paints.
 */
export function injectPartnerFonts(doc: Document = document): void {
  const faces = fonts.faces
    .map((face) => {
      // woff2 is required; woff is only emitted when the face ships one, so a
      // variable/woff2-only face never renders `url(undefined)`.
      const src = [
        `url(${face.woff2}) format('woff2')`,
        face.woff && `url(${face.woff}) format('woff')`
      ]
        .filter(Boolean)
        .join(', ');

      return `@font-face {
  font-family: '${face.family}';
  font-style: ${face.style ?? 'normal'};
  font-display: swap;
  font-weight: ${face.weight};
  src: ${src};
  unicode-range: U+0020-017F, U+20AC;
}`;
    })
    .join('\n');

  const { base, baseline, title, body, caption } = fonts.stacks;
  const vars = `:root {
  --font-base: ${base};
  --font-baseline: ${baseline};
  --font-title: ${title};
  --font-body: ${body};
  --font-caption: ${caption};
}`;

  const style = doc.createElement('style');
  style.dataset.partnerFonts = '';
  style.appendChild(doc.createTextNode(`${faces}\n\n${vars}`));
  doc.head.appendChild(style);
}

export default injectPartnerFonts;
